import "./Search.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import SearchFilter from "../../Components/Shared/Search/Search";
import VivahApi from "../../api";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { calculateAgeInYearsAndMonths } from "../../utils/common";

const PAGE_COUNT = 8;

function buildLookup(items) {
  const map = {};
  (items || []).forEach((i) => {
    map[String(i.sid)] = i.name || i.label || String(i.sid);
  });
  return map;
}

function SkeletonCard() {
  return (
    <div className="col-xs-12 col-sm-6 col-md-3">
      <div className="profile-card skeleton-card">
        <div className="skeleton skeleton-photo"></div>
        <div className="profile-card-body">
          <div className="skeleton skeleton-line wide"></div>
          <div className="skeleton skeleton-line medium"></div>
          <div className="skeleton skeleton-line narrow"></div>
          <div className="skeleton skeleton-line medium"></div>
          <div className="profile-card-actions">
            <div className="skeleton skeleton-btn"></div>
            <div className="skeleton skeleton-btn"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchPage() {
  const [profiles, setProfiles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [sortFilter, setSortFilter] = useState("sid DESC");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);  // has user submitted a search yet?
  const [masterData, setMasterData] = useState(null);
  const [favourites, setFavourites] = useState({});
  const [interests, setInterests] = useState({});
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);

  useEffect(() => {
    // Show profile completion prompt once per session
    if (!sessionStorage.getItem('profilePromptShown')) {
      VivahApi.Auth.userMe().then((res) => {
        const user = res.data?.user || res.data;
        if (!user?.profileComplete) {
          setShowProfilePrompt(true);
          sessionStorage.setItem('profilePromptShown', '1');
        }
      }).catch(() => {});
    }

    VivahApi.profile.listProfile().then((res) => setMasterData(res.data));
    VivahApi.Favourite.listFavourite()
      .then((res) => {
        const map = {};
        (res.data.data || []).forEach((f) => { map[f.profileId] = true; });
        setFavourites(map);
      })
      .catch(() => {});
    // Pre-load sent interests so button shows correct state after reload
    VivahApi.Interest.getRequest('sent')
      .then((res) => {
        const map = {};
        (res.data.data || []).forEach((i) => { map[i.toUserId] = true; });
        setInterests(map);
      })
      .catch(() => {});
  }, []);

  const lookups = useMemo(() => {
    if (!masterData) return {};
    return {
      religion: buildLookup(masterData.religions),
      qualification: buildLookup(masterData.qualifications),
      profession: buildLookup(masterData.professions),
      city: buildLookup(masterData.cities),
      state: buildLookup(masterData.states),
    };
  }, [masterData]);

  const resolve = useCallback(
    (key, id) => (id && lookups[key] ? lookups[key][String(id)] || "" : ""),
    [lookups]
  );

  const fetchResults = useCallback(
    async (pageNum, sort) => {
      setLoading(true);
      try {
        const res = await VivahApi.Search.getSearch(sort, PAGE_COUNT, (pageNum - 1) * PAGE_COUNT);
        setProfiles(res.data.profiles || []);
        setTotalCount(res.data.count || 0);
        setTotalPage(Math.ceil((res.data.count || 0) / PAGE_COUNT));
      } catch {
        setProfiles([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Called by the Search filter form after saving preferences
  const handleSearch = useCallback(() => {
    setPage(1);
    setSearched(true);
    fetchResults(1, sortFilter);
    // Scroll down to results smoothly
    setTimeout(() => {
      document.getElementById("sp-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [fetchResults, sortFilter]);

  // Re-fetch when page or sort changes (only if a search has been run)
  useEffect(() => {
    if (searched) fetchResults(page, sortFilter);
  }, [page, sortFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFavourite = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const profileId = p.sid || p.userId;
    if (favourites[profileId]) {
      await VivahApi.Favourite.removeFavourite(profileId).catch(() => {});
      setFavourites((prev) => { const n = { ...prev }; delete n[profileId]; return n; });
    } else {
      await VivahApi.Favourite.addFavourite(profileId).catch(() => {});
      setFavourites((prev) => ({ ...prev, [profileId]: true }));
    }
  };

  const handleSendInterest = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const profileId = p.sid || p.userId;
    if (interests[profileId]) return;
    try {
      await VivahApi.Interest.addInterest(profileId);
      setInterests((prev) => ({ ...prev, [profileId]: true }));
    } catch (err) {
      // Backend returns 400 when interest already exists
      if (err?.response?.status === 400 || err?.response?.status === 409)
        setInterests((prev) => ({ ...prev, [profileId]: true }));
    }
  };

  const fromResult = totalCount === 0 ? 0 : (page - 1) * PAGE_COUNT + 1;
  const toResult = Math.min(page * PAGE_COUNT, totalCount);

  return (
    <>
      {showProfilePrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: '36px 32px',
            maxWidth: 420, width: '90%', textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👤</div>
            <h3 style={{ marginBottom: 8, color: '#333' }}>Complete Your Profile</h3>
            <p style={{ color: '#666', marginBottom: 24, lineHeight: 1.6 }}>
              Without a complete profile we can't show you the most relevant matches.
              Take a moment to fill in your details to get the best experience.
            </p>
            <a
              href="/update-profile"
              style={{
                display: 'inline-block', padding: '10px 28px',
                backgroundColor: '#e91e63', color: '#fff',
                borderRadius: 5, textDecoration: 'none', fontWeight: 600,
                marginRight: 12,
              }}
            >
              Complete Profile
            </a>
            <button
              onClick={() => setShowProfilePrompt(false)}
              style={{
                padding: '10px 20px', border: '1px solid #ccc',
                borderRadius: 5, background: '#f5f5f5',
                color: '#555', cursor: 'pointer',
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />

        {/* <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Search Profiles</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="/">Home</a><span>Search</span>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <section id="gla_content" className="gla_content page-top-offset">
          <section className="gla_section">
            <div className="container">

              {/* ── Filter panel ──────────────────────────────────────── */}
              <div className="sp-filter-panel">
                <div className="sp-filter-header">
                  <h3 className="sp-filter-title">
                    <i className="ti ti-filter" /> Filter Profiles
                  </h3>
                  <p className="sp-filter-hint">
                    Your preferences are saved automatically. Visit{" "}
                    <Link to="/discover">Discover</Link> anytime to see
                    auto-suggested profiles based on these filters.
                  </p>
                </div>
                <SearchFilter onSearch={handleSearch} />
              </div>

              {/* ── Results area ──────────────────────────────────────── */}
              <div id="sp-results" className="sp-results">
                {!searched && !loading && (
                  <div className="sp-prompt">
                    <i className="ti ti-search" />
                    <p>Adjust your filters above and press <strong>Search</strong> to see matching profiles.</p>
                    <Link className="sp-prompt-cta" to="/discover">
                      <i className="ti ti-heart" /> View auto-suggested Discover feed instead
                    </Link>
                  </div>
                )}

                {(searched || loading) && (
                  <>
                    {/* Results header bar */}
                    <div className="matches-header">
                      <div className="matches-count">
                        {loading
                          ? "Searching…"
                          : totalCount === 0
                          ? "No profiles match your filters"
                          : `Showing ${fromResult}–${toResult} of ${totalCount} profiles`}
                      </div>
                      <div className="matches-sort">
                        <label htmlFor="sp-sort">Sort by</label>
                        <select
                          id="sp-sort"
                          value={sortFilter}
                          onChange={(e) => { setPage(1); setSortFilter(e.target.value); }}
                        >
                          <option value="sid DESC">Newest first</option>
                          <option value="age DESC">Age: high to low</option>
                          <option value="age ASC">Age: low to high</option>
                        </select>
                      </div>
                    </div>

                    {/* Cards */}
                    <div className="row matches-grid">
                      {loading &&
                        Array.from({ length: PAGE_COUNT }).map((_, i) => (
                          <SkeletonCard key={i} />
                        ))}

                      {!loading && totalCount === 0 && (
                        <div className="matches-empty">
                          <i className="ti ti-search"></i>
                          <h3>No profiles found</h3>
                          <p>Try broadening your filters — fewer restrictions usually reveal more matches.</p>
                        </div>
                      )}

                      {!loading &&
                        profiles.map((p) => {
                          const profileId = p.sid || p.userId;
                          const name =
                            p.name ||
                            `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
                            "Unknown";
                          const age = p.dateOfBirth
                            ? calculateAgeInYearsAndMonths(new Date(p.dateOfBirth))
                            : null;
                          const religion = resolve("religion", p.religionId);
                          const location = [
                            resolve("city", p.cityId),
                            resolve("state", p.stateId),
                          ]
                            .filter(Boolean)
                            .join(", ");
                          const qualification = resolve("qualification", p.qualificationId);
                          const profession = resolve("profession", p.professionId);
                          const isFav = !!favourites[profileId];
                          const sentInterest = !!interests[profileId];

                          return (
                            <div className="col-xs-12 col-sm-6 col-md-3" key={profileId}>
                              <div className="profile-card">
                                <a
                                  href={`/profile/${p.sid}`}
                                  className="profile-card-photo-wrap"
                                >
                                  <img
                                    src={p.profilePhoto || "/assets/images/avatar-placeholder.svg"}
                                    alt={name}
                                    className="profile-card-photo"
                                  />
                                  <span className="profile-card-badge">Premium</span>
                                </a>
                                <div className="profile-card-body">
                                  <a href={`/profile/${p.sid}`} className="profile-card-name">
                                    {name}
                                  </a>
                                  {age && <span className="profile-card-age">{age}</span>}
                                  <div className="profile-card-chips">
                                    {religion && (
                                      <span className="pcc">
                                        <i className="ti ti-shine"></i> {religion}
                                      </span>
                                    )}
                                    {location && (
                                      <span className="pcc">
                                        <i className="ti ti-location-pin"></i> {location}
                                      </span>
                                    )}
                                    {qualification && (
                                      <span className="pcc">
                                        <i className="ti ti-shield"></i> {qualification}
                                      </span>
                                    )}
                                    {profession && (
                                      <span className="pcc">
                                        <i className="ti ti-briefcase"></i> {profession}
                                      </span>
                                    )}
                                  </div>
                                  <div className="profile-card-actions">
                                    <button
                                      className={`pca-fav${isFav ? " active" : ""}`}
                                      onClick={(e) => handleFavourite(e, p)}
                                      title={isFav ? "Remove from favourites" : "Add to favourites"}
                                    >
                                      <i className={`ti ti-heart${isFav ? "" : "-broken"}`}></i>
                                      {isFav ? "Saved" : "Save"}
                                    </button>
                                    <button
                                      className={`pca-interest${sentInterest ? " sent" : ""}`}
                                      onClick={(e) => handleSendInterest(e, p)}
                                      disabled={sentInterest}
                                    >
                                      <i className="ti ti-email"></i>
                                      {sentInterest ? "Sent" : "Send Interest"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Pagination */}
                    {!loading && totalPage > 1 && (
                      <nav className="matches-pagination">
                        <button
                          className="mpag-btn"
                          onClick={() => setPage((p) => p - 1)}
                          disabled={page === 1}
                        >
                          <i className="ti ti-angle-left"></i> Prev
                        </button>
                        <div className="mpag-pages">
                          {Array.from({ length: totalPage }).map((_, i) => (
                            <button
                              key={i}
                              className={`mpag-page${page === i + 1 ? " active" : ""}`}
                              onClick={() => setPage(i + 1)}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                        <button
                          className="mpag-btn"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={page === totalPage}
                        >
                          Next <i className="ti ti-angle-right"></i>
                        </button>
                      </nav>
                    )}
                  </>
                )}
              </div>

            </div>
          </section>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default SearchPage;
